import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  CheckCircle2, Circle, Loader2, ShieldCheck, Landmark, Search, BarChart3, FileCheck, ArrowLeft,
  CreditCard, TrendingDown, AlertTriangle, PartyPopper, XCircle
} from "lucide-react";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/onboarding`;

const STEP_ICONS = {
  ein_verify: ShieldCheck,
  ofac_screen: ShieldCheck,
  address_verify: Search,
  bank_verify: Landmark,
  bureau_pull: BarChart3,
  funding_verify: Search,
  credit_model: TrendingDown,
  decision: FileCheck,
};

export default function ApplicationStatus() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const [pipeline, setPipeline] = useState([]);
  const [status, setStatus] = useState("submitted");
  const [decision, setDecision] = useState(null);
  const [appData, setAppData] = useState(null);
  const [animating, setAnimating] = useState(false);
  const [allComplete, setAllComplete] = useState(false);
  const intervalRef = useRef(null);

  // Load initial state
  useEffect(() => {
    const load = async () => {
      try {
        const [pRes, aRes] = await Promise.all([
          axios.get(`${API}/applications/${appId}/pipeline`),
          axios.get(`${API}/applications/${appId}`),
        ]);
        setPipeline(pRes.data.pipeline || []);
        setStatus(pRes.data.status);
        setDecision(pRes.data.decision);
        setAppData(aRes.data);

        const done = (pRes.data.pipeline || []).every(s => s.status === "completed");
        setAllComplete(done);
        if (!done && pRes.data.status === "submitted") {
          startAnimation();
        }
      } catch (e) { console.error(e); }
    };
    load();
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [appId]);

  const startAnimation = useCallback(() => {
    if (animating) return;
    setAnimating(true);

    const advance = async () => {
      try {
        const res = await axios.post(`${API}/applications/${appId}/pipeline/advance`);
        setPipeline(res.data.pipeline);
        if (res.data.all_complete) {
          setAllComplete(true);
          setAnimating(false);
          if (intervalRef.current) clearInterval(intervalRef.current);
          // Fetch final decision
          const pRes = await axios.get(`${API}/applications/${appId}/pipeline`);
          setDecision(pRes.data.decision);
          setStatus(pRes.data.status);
        }
      } catch (e) {
        console.error(e);
        if (intervalRef.current) clearInterval(intervalRef.current);
        setAnimating(false);
      }
    };

    // Each step needs 2 calls: pending->running, running->completed
    // So for 8 steps we need 16 calls, but let's use a faster pace
    intervalRef.current = setInterval(advance, 1200);
  }, [appId, animating]);

  const completedCount = pipeline.filter(s => s.status === "completed").length;
  const totalSteps = pipeline.length;
  const progressPct = totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;

  const fmtCurrency = (v) => {
    if (!v) return "—";
    if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
    if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
    return `$${v.toLocaleString()}`;
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="application-status-page">
      <header className="sticky top-0 z-40 bg-white border-b border-[#E2E8F0] px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="h-8 text-xs text-[#64748B]" onClick={() => navigate("/onboarding")} data-testid="back-btn"><ArrowLeft className="h-3.5 w-3.5 mr-1" />Applications</Button>
            <Separator orientation="vertical" className="h-5" />
            <div>
              <h1 className="text-base font-semibold text-[#502D3C]">Application Review Pipeline</h1>
              <p className="text-[10px] text-[#64748B]">{appData?.section1a?.legal_name || "Loading..."} — ID: {appId}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {status === "approved" && <Badge className="bg-green-100 text-green-700 border-green-200 h-6">Approved</Badge>}
            {status === "declined" && <Badge className="bg-red-100 text-red-700 border-red-200 h-6">Declined</Badge>}
            {status === "submitted" && !allComplete && <Badge className="bg-blue-100 text-blue-700 border-blue-200 h-6"><Loader2 className="h-3 w-3 animate-spin mr-1" />Processing</Badge>}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Progress bar */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-[#502D3C]">Review Progress</span>
              <span className="text-sm font-bold text-[#502D3C]">{progressPct}%</span>
            </div>
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#502D3C] to-[#FFCC1C] rounded-full transition-all duration-700 ease-out" style={{ width: `${progressPct}%` }} data-testid="progress-bar" />
            </div>
            <p className="text-[10px] text-[#94A3B8] mt-1.5">{completedCount} of {totalSteps} verification steps complete</p>
          </CardContent>
        </Card>

        {/* Pipeline Steps */}
        <Card data-testid="pipeline-steps">
          <CardHeader className="pb-2 pt-4"><CardTitle className="text-sm font-semibold text-[#502D3C]">Verification Pipeline</CardTitle></CardHeader>
          <CardContent className="pb-5">
            <div className="space-y-0">
              {pipeline.map((step, i) => {
                const Icon = STEP_ICONS[step.id] || Circle;
                const isRunning = step.status === "running";
                const isDone = step.status === "completed";
                const isPending = step.status === "pending";

                return (
                  <div key={step.id} className="relative" data-testid={`pipeline-step-${step.id}`}>
                    {/* Connector line */}
                    {i < pipeline.length - 1 && (
                      <div className={cn("absolute left-[17px] top-10 w-0.5 h-[calc(100%-8px)]", isDone ? "bg-green-300" : "bg-gray-200")} />
                    )}
                    <div className={cn("flex items-start gap-4 p-3 rounded-lg transition-all duration-500", isRunning ? "bg-[#502D3C]/5 border border-[#502D3C]/10" : isDone ? "bg-green-50/50" : "")}>
                      <div className={cn("w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500",
                        isDone ? "bg-green-100" : isRunning ? "bg-[#502D3C]/10" : "bg-gray-100")}>
                        {isDone ? <CheckCircle2 className="h-5 w-5 text-green-600" /> :
                         isRunning ? <Loader2 className="h-5 w-5 text-[#502D3C] animate-spin" /> :
                         <Icon className="h-4 w-4 text-[#94A3B8]" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={cn("text-sm font-medium", isDone ? "text-green-700" : isRunning ? "text-[#502D3C]" : "text-[#94A3B8]")}>{step.name}</p>
                          {isRunning && <Badge className="bg-[#FFCC1C] text-[#502D3C] text-[8px] h-4 animate-pulse">In Progress</Badge>}
                        </div>
                        <p className={cn("text-xs mt-0.5", isDone ? "text-green-600/80" : isRunning ? "text-[#64748B]" : "text-[#CBD5E1]")}>{step.description}</p>
                        {/* Step result */}
                        {isDone && step.result && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {step.id === "ein_verify" && step.result.verified && <ResultPill color="green">IRS Match Confirmed</ResultPill>}
                            {step.id === "ofac_screen" && step.result.clear && <ResultPill color="green">No matches ({step.result.databases_checked?.length} databases)</ResultPill>}
                            {step.id === "address_verify" && <ResultPill color="green">{step.result.usps_match}</ResultPill>}
                            {step.id === "bank_verify" && <ResultPill color="green">{step.result.method}</ResultPill>}
                            {step.id === "bureau_pull" && (
                              <>
                                {step.result.thin_file ? <ResultPill color="amber">Thin File — Model Reweighted</ResultPill> : (
                                  <>
                                    <ResultPill color="green">PAYDEX: {step.result.dnb_paydex}</ResultPill>
                                    <ResultPill color="green">Intelliscore: {step.result.experian_intelliscore}</ResultPill>
                                    <ResultPill color="blue">{step.result.trade_lines} trade lines</ResultPill>
                                  </>
                                )}
                              </>
                            )}
                            {step.id === "funding_verify" && (
                              <>
                                {step.result.sec_edgar_found && <ResultPill color="green">SEC Form D Found</ResultPill>}
                                {step.result.crunchbase_match && <ResultPill color="green">Crunchbase Match</ResultPill>}
                                {!step.result.discrepancy && <ResultPill color="green">No Discrepancies</ResultPill>}
                              </>
                            )}
                            {step.id === "credit_model" && (
                              <>
                                <ResultPill color="blue">PD: {step.result.pd}%</ResultPill>
                                <ResultPill color="blue">{step.result.risk_class}</ResultPill>
                              </>
                            )}
                            {step.id === "decision" && (
                              step.result.approved ?
                                <ResultPill color="green">Approved — Limit: {fmtCurrency(step.result.credit_limit)}</ResultPill> :
                                <ResultPill color="red">Declined</ResultPill>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Decision Card */}
        {allComplete && decision && (
          <Card className={cn("border-2 transition-all duration-700", decision.approved ? "border-green-200 bg-green-50/30" : "border-red-200 bg-red-50/30")} data-testid="decision-card">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className={cn("w-14 h-14 rounded-2xl flex items-center justify-center", decision.approved ? "bg-green-100" : "bg-red-100")}>
                  {decision.approved ? <PartyPopper className="h-7 w-7 text-green-600" /> : <XCircle className="h-7 w-7 text-red-600" />}
                </div>
                <div className="flex-1">
                  <h2 className={cn("text-xl font-bold", decision.approved ? "text-green-700" : "text-red-700")}>
                    {decision.approved ? "Application Approved" : "Application Declined"}
                  </h2>
                  <p className="text-sm text-[#64748B] mt-1">
                    {decision.approved
                      ? `${appData?.section1a?.legal_name || "Your company"} has been approved for the Rippling Corporate Card.`
                      : `We were unable to approve the application at this time. ${decision.decline_reason || ""}`
                    }
                  </p>

                  {decision.approved && (
                    <div className="mt-4 grid grid-cols-4 gap-4">
                      <DecisionMetric label="Credit Limit" value={fmtCurrency(decision.credit_limit)} />
                      <DecisionMetric label="Risk Class" value={decision.risk_class} />
                      <DecisionMetric label="PD" value={`${decision.pd}%`} />
                      <DecisionMetric label="Billing Cycle" value={decision.billing_cycle} />
                      <DecisionMetric label="Expected Exposure" value={fmtCurrency(decision.ead)} />
                      <DecisionMetric label="LGD" value={`${(decision.lgd * 100).toFixed(1)}%`} />
                      <DecisionMetric label="ECL" value={fmtCurrency(decision.ecl)} />
                      <DecisionMetric label="Bureau" value={decision.bureau?.thin_file ? "Thin File (reweighted)" : "Full File"} />
                    </div>
                  )}

                  {decision.approved && (
                    <div className="mt-5 bg-white rounded-lg border border-green-200 p-4">
                      <h4 className="text-xs font-semibold text-[#502D3C] mb-2">What happens next?</h4>
                      <div className="grid grid-cols-2 gap-3 text-xs text-[#64748B]">
                        <div className="flex items-start gap-2"><CreditCard className="h-4 w-4 text-[#502D3C] mt-0.5 flex-shrink-0" /><span>Virtual cards are available immediately in your Rippling dashboard.</span></div>
                        <div className="flex items-start gap-2"><Landmark className="h-4 w-4 text-[#502D3C] mt-0.5 flex-shrink-0" /><span>Physical cards will be mailed within 5-7 business days.</span></div>
                        <div className="flex items-start gap-2"><ShieldCheck className="h-4 w-4 text-[#502D3C] mt-0.5 flex-shrink-0" /><span>Your card admin can set per-employee limits and category restrictions.</span></div>
                        <div className="flex items-start gap-2"><BarChart3 className="h-4 w-4 text-[#502D3C] mt-0.5 flex-shrink-0" /><span>Credit limit can increase after 3 months of on-time payments.</span></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex justify-center pb-8">
          <Button variant="outline" onClick={() => navigate("/onboarding")} data-testid="back-to-apps-btn"><ArrowLeft className="h-4 w-4 mr-1.5" />Back to Applications</Button>
        </div>
      </main>
    </div>
  );
}

function ResultPill({ children, color }) {
  const colors = {
    green: "bg-green-100 text-green-700 border-green-200",
    amber: "bg-amber-100 text-amber-700 border-amber-200",
    blue: "bg-blue-100 text-blue-700 border-blue-200",
    red: "bg-red-100 text-red-700 border-red-200",
  };
  return <span className={cn("text-[10px] px-2 py-0.5 rounded-full border font-medium", colors[color])}>{children}</span>;
}

function DecisionMetric({ label, value }) {
  return (
    <div className="bg-white rounded-lg border border-gray-100 p-3">
      <p className="text-[9px] text-[#64748B] uppercase tracking-wider">{label}</p>
      <p className="text-sm font-bold text-[#502D3C] mt-0.5">{value}</p>
    </div>
  );
}
